import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
}

function getOrCreateMeta(selector: string, attr: 'name' | 'property', value: string): HTMLMetaElement {
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, value);
    el.setAttribute('data-seo', 'dynamic');
    document.head.appendChild(el);
  }
  return el;
}

function getOrCreateLink(rel: string): HTMLLinkElement {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    el.setAttribute('data-seo', 'dynamic');
    document.head.appendChild(el);
  }
  return el;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  noIndex,
}: SEOProps) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    getOrCreateMeta('meta[name="description"]', 'name', 'description').content = description;

    if (keywords) {
      getOrCreateMeta('meta[name="keywords"]', 'name', 'keywords').content = keywords;
    }

    if (noIndex) {
      getOrCreateMeta('meta[name="robots"]', 'name', 'robots').content = 'noindex, nofollow';
    } else {
      const robots = document.head.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
      if (robots && robots.getAttribute('data-seo') === 'dynamic') {
        robots.content = 'index, follow';
      }
    }

    // Open Graph
    getOrCreateMeta('meta[property="og:type"]', 'property', 'og:type').content = ogType;
    getOrCreateMeta('meta[property="og:title"]', 'property', 'og:title').content = ogTitle || title;
    getOrCreateMeta('meta[property="og:description"]', 'property', 'og:description').content = ogDescription || description;
    if (ogImage) {
      getOrCreateMeta('meta[property="og:image"]', 'property', 'og:image').content = ogImage;
    }

    // Twitter
    getOrCreateMeta('meta[name="twitter:title"]', 'name', 'twitter:title').content = ogTitle || title;
    getOrCreateMeta('meta[name="twitter:description"]', 'name', 'twitter:description').content = ogDescription || description;
    if (ogImage) {
      getOrCreateMeta('meta[name="twitter:image"]', 'name', 'twitter:image').content = ogImage;
    }

    // Canonical
    const canonicalLink = getOrCreateLink('canonical');
    canonicalLink.href = canonical ? `${window.location.origin}${canonical}` : window.location.href;

    // Last-modified
    getOrCreateMeta('meta[name="last-modified"]', 'name', 'last-modified').content = new Date().toISOString().split('T')[0];

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogType, noIndex]);
}